import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, useLogout, AUTH_ROUTES } from "@ticketon/authentication-core";
import { Profile, type ProfileMenuItem, LanguageSelector, USFlagIcon, MXFlagIcon } from "ui";
import { Menu } from "lucide-react";
import { cn } from "@/utils/cn";

interface IAdminHeaderProps {
  sidebarCollapsed: boolean;
  isMobile: boolean;
  onMenuClick: () => void;
}

const LANGUAGES = [
  { value: "es", label: "Español", icon: <MXFlagIcon size={20} /> },
  { value: "en", label: "English", icon: <USFlagIcon size={20} /> },
];

export function AdminHeader({ sidebarCollapsed, isMobile, onMenuClick }: IAdminHeaderProps) {
  const { t, i18n } = useTranslation();
  const { user, clearAuth, sessionBridge } = useAuth();
  const { logout } = useLogout();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Ignore backend logout errors; always clear the session locally.
    } finally {
      clearAuth();
      sessionBridge?.clear();
      navigate(AUTH_ROUTES.LOGIN);
    }
  };

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
    localStorage.setItem("i18nextLng", language);
  };

  const menuItems: ProfileMenuItem[] = [
    {
      id: "profile",
      label: t("profile"),
      type: "profile",
    },
    {
      id: "logout",
      label: t("logout"),
      type: "action",
    },
  ];

  const handleMenuItemClick = (item: ProfileMenuItem) => {
    if (item.id === "logout") {
      handleLogout();
    }
  };

  const profileUser = {
    name: user?.firstName
      ? `${user.firstName} ${user.lastName || ""}`.trim()
      : t("header.default_user"),
    initials: user?.firstName
      ? `${user.firstName.charAt(0)}${user.lastName?.charAt(0) || ""}`.toUpperCase()
      : "U",
  };

  const currentLang = i18n.language?.substring(0, 2) || "es";

  return (
    <header
      className={cn(
        "fixed top-0 right-0 z-30 h-16 bg-white border-b border-gray-200 transition-all duration-300",
        isMobile ? "left-0" : (sidebarCollapsed ? "left-16" : "left-64")
      )}
    >
      <div className="flex h-full items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-3">
          {/* Mobile menu button */}
          {isMobile && (
            <button
              onClick={onMenuClick}
              className="p-2 -ml-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              aria-label={t("sidebar.open")}
            >
              <Menu size={24} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <LanguageSelector
            currentLanguage={currentLang}
            languages={LANGUAGES}
            onLanguageChange={handleLanguageChange}
          />

          <Profile
            user={profileUser}
            menuItems={menuItems}
            onMenuItemClick={handleMenuItemClick}
          />
        </div>
      </div>
    </header>
  );
}
